import Intercom from "helpers/Intercom";
import { ReactElement, ReactNode } from "react";
import package_json from "../../package.json";
import { BrandUser } from "api/brandApi";
import { SymphonyLogoStyleKey } from "pages/WebsitePage/components/SymphonyLogoEditorCard";
import { AdPlatform, CampaignStatus, CampaignType, fontThemeStyles, OrganizationRole, PlanType, PlaybookStepName, UserPermissionOnBrand } from "./Const";
import { PlaybookType, StreamingPlatform } from "./Const";
import { ThemeStyleKey } from "pages/WebsitePage/components/CustomizeWebsite/components/StyleSection/ThemeSelectorSection/utils";
import { BillingCycle } from "components/shareable/UpgradeToProModal/utils";
import { AssetPlatformType } from "components/CampaignViews/AdCreativeSelectorCampaignView";
import { FacebookCampaignSubmissionType, TargetedAudienceType, TikTokCampaignSubmissionType, YouTubeCampaignSubmissionType } from "./Campaign";

export const VERSION = package_json.version.split(".").splice(0, 2).join(".");

//TODO: Add DotProps from recharts types
export type DotProps = any;

/** Price data structure for playlisting campaign */
interface PriceData {
    /** The tier number for the campaign */
    tier: number;
    /** Cost of the campaign */
    price: number;
    /** Display name for the tier including follower count */
    name: string;
    /** Description of the campaign including track name */
    description: string;
}

/** Content details for the playlisting campaign */
export interface PlaylistingCampaignContent {
    /** Spotify ID of the track */
    spotifyId: string;
    /** Name of the track */
    name: string;
    /** Internal ID of the track */
    id: string;
    /** URL slug for the track */
    slug: string;
    /** URL of the track thumbnail */
    thumbnail_url: string;
    /** Spotify URL of the track */
    url: string;
}

/** Input structure for creating a playlisting campaign */
export interface PlaylistingCampaignInput {
    /** Campaign ID if already created */
    id: string | null;
    /** Budget for the campaign */
    budget: number;
    /** Playbook type */
    playbookType: PlaybookType;
    /** Type of campaign */
    campaign_type: 'get_playlisted';
    /** Version of the campaign */
    campaign_version: 'v2';
    /** Notes for the pitch */
    pitch_notes: string;
    /** Price and tier information */
    price_data: PriceData;
    /** Track content information */
    content: PlaylistingCampaignContent;
    /** Platforms where campaign will run */
    campaign_platforms: ['playlisting'];
}

export enum AdConnectionOptions {
    express = 'express',
    custom = 'custom'
}
/** Campaign price and description information */
interface CampaignPriceData {
    price: number;
    name: string;
    description: string;
}

/** Campaign logistics configuration */
interface CampaignLogistics {
    refundFeeConfirmed: boolean;
    confirmedCampaignSettings: boolean;
    budget: number;
    startDate: string;
    endDate: string;
}

/** Campaign content details */
interface CampaignContent {
    name: string | undefined;
    id: string | undefined;
    thumbnail_url: string | undefined;
    channelId: string | undefined;
    channelTitle: string | undefined;
}

/** Campaign geography settings */
interface CampaignGeography {
    type: string;
    countries: string[];
}

/** Complete campaign configuration */
export interface YouTubeCampaignData {
    budget: number;
    id?: number;
    campaign_type: 'increase_video_views';
    playbookType: PlaybookType;
    price_data: CampaignPriceData;
    logistics: CampaignLogistics;
    content: CampaignContent;
    targeting: unknown; // Type this based on your targeting structure
    targetingType: string;
    geographies: CampaignGeography;
    campaign_platforms: ['youtube'];
    payment_method: 'paypal' | 'stripe';
}

/** Interface for TikTok identity info */
interface TikTokUserInfo {
    identity_type: string;
    identity_id: string;
    tiktok_name: string;
    profile_image: string;
    identity_display_name: string;
    identity_info?: {
        avatar_url?: string;
        follower_count?: number;
        following_count?: number;
        likes_count?: number;
        video_count?: number;
    };
}


/** Interface for TikTok video information */
interface TikTokVideoInfo {
    file_name: string;
    poster_url: string;
    bit_rate: number;
    size: number;
    width: number;
    height: number;
    signature: string;
    duration: number;
    preview_url: string;
    video_id: string;
}

/** Interface for TikTok item information */
interface TikTokItemInfo {
    text: string;
    anchor_list: null | any; // TODO: Define specific type
    stitch_original_item_id: string;
    auth_code: string;
    duet_original_item_id: string;
    item_id: string;
    is_multi_duet_stitch: boolean;
    mentioned_item_ids: null | any; // TODO: Define specific type
}

/** Interface for TikTok Spark Ad data */
export interface TikTokSparkAd {
    auth_info: {
        auth_start_time: string;
        auth_end_time: string;
        auth_status?: string;
    };
    user_info: TikTokUserInfo;
    video_info?: TikTokVideoInfo;
    item_info?: TikTokItemInfo;
    platformType: AssetPlatformType.TIKTOK
}

export interface SpotifyArtistsOption {
    id: string;
    name: string;
    followers: number | null;
    genres: string[] | null;
    thumbnail_url: string | null;
}

export type FBLocation = {
    key: string
    name: string
    type: string
    country_code: string
    country_name: string
    region: string
    region_id: number
    supports_city: boolean
}

export type LocationsResponse = {
    data: FBLocation[]
}

export type Location = {
    value: string
    label: string
    type: string
    country_code: string | null
    region_id: string | null
}

export type GeographicTargetingType = {
    id?: string
    name: string
    budget: number
    description?: string
    locations: { [key: string]: Location }
}

export type AreaDot =
    | ReactElement<SVGElement>
    | ((props: any) => ReactElement<SVGElement>)
    | ((props: any) => ReactElement<SVGElement>)
    | DotProps
    | boolean;

export type IObjectKeys = { [key: string]: any };
export type TooltipType = "none";
export type ValueType = number | string | Array<number | string>;
export type NameType = number | string;

export type Formatter<TValue extends ValueType, TName extends NameType> = (
    value: TValue,
    name: TName,
    item: Payload<TValue, TName>,
    index: number,
    payload: Array<Payload<TValue, TName>>
) => [ReactNode, ReactNode] | ReactNode;
export interface Payload<TValue extends ValueType, TName extends NameType> {
    type?: TooltipType;
    color?: string;
    formatter?: Formatter<TValue, TName>;
    name?: TName;
    value?: TValue;
    unit?: ReactNode;
    dataKey?: string | number;
    payload?: IObjectKeys;
    chartType?: string;
}

/** Comprehensive interface for website styling configuration */
export interface WebsiteStyle {
    /** Selected theme template */
    template?: ThemeStyleKey;
    /** Button style configuration (e.g. 'boxes', 'rounded') */
    buttonConfig?: string;
    /** Background type (color or artwork) */
    bgType?: string;
    /** Background color value */
    bgColor?: string;
    /** Primary brand color */
    primaryColor?: string;
    /** Secondary brand color */
    secondaryColor?: string;
    /** Font theme style */
    font?: string;
    /** Title text alignment */
    titleAlignment?: 'left' | 'center' | 'right';
    /** Title text content */
    titleText?: string;
    /** Subtitle text content */
    subtitle?: string;
    /** Whether to show secondary line */
    showSecondaryLine?: boolean;
    /** Custom thumbnail URL */
    thumbnail_url?: string;
    /** YouTube embed configuration */
    youtubeEmbed?: {
        /** YouTube video URL */
        url?: string;
        /** YouTube video ID */
        id?: string;
    };
    /** Full video URL */
    full_video_url?: string;
    /** Submit button text */
    submit_button_text?: string;
}


/** 
 * Represents the simplified record details returned by getRecordDetails_api
 * Contains subset of ContentMetadata fields plus some additional fields
 */
export interface RecordDetails {
    id: number;
    /** Whether full record information is available */
    full_info: boolean;
    /** Record name */
    name: string;
    /** Record URL */
    url: string;
    /** Record slug identifier */
    slug: string;
    /** Thumbnail image URL */
    thumbnail_url: string;
    /** Spotify release classification */
    spotify_release_type: string;
    /** Optional PRID identifier */
    prid?: string;
    // Inherited from ContentMetadata
    spotify_id: string;
    spotify_uri: string;
    record_url: string;
    primary_artist: string;
    preview_url: string;
    type: 'single' | 'record' | 'album' | 'project' | 'playlist';
    updateCount: number;
    external_links: ExternalLink[];
    tracks?: any[];
    external_ids: {
        isrc?: string;
        upc?: string;
    } | Record<string, unknown>;
}

/** Interface for tour date information */
export interface TourDate {
    /** City where event takes place */
    city: string;
    /** Country where event takes place */
    country: string;
    /** Date of the event */
    date: string;
    /** Display name for the event */
    displayName: string;
    /** Whether email/number collection is enabled */
    isEnableEmailNumberCollection?: boolean;
    /** Whether presale period is active */
    isPreSalePeriod: boolean;
    /** Action for presale */
    preSaleAction: 'collect' | 'link';
    /** Whether event is sold out */
    isSoldOut: boolean;
    /** Date when presale starts */
    preSaleDate: string | null;
    /** Text for presale button */
    preSaleButtonText: string;
    /** Custom link for presale */
    preSaleCustomLink: string;
    /** Region where event takes place */
    region: string;
    /** URL to purchase tickets */
    ticketUrl: string;
    /** Venue name */
    venue: string;
    /** Text for ticket button */
    buttonLinkText?: string;
    /** Source of tour date data */
    source?: string;
}

/** Unified content metadata for records, albums, projects and playlists */
export interface ContentMetadata {
    /** Optional reason for publishing */
    publishReason?: string;
    /** Whether content is published */
    published?: boolean;
    /** Content name */
    name: string;
    /** Spotify ID */
    spotify_id: string;
    /** Spotify URI */
    spotify_uri: string;
    /** Thumbnail image URL */
    thumbnail_url: string;
    /** Record/content URL */
    record_url: string;
    /** Primary artist name */
    primary_artist: string;
    /** Preview audio URL */
    preview_url: string;
    /** Content type (single, record, album, project, playlist) */
    type: 'single' | 'record' | 'album' | 'project' | 'playlist';
    /** Number of times content has been updated */
    updateCount: number;
    /** Array of external links */
    external_links: ExternalLink[];
    /** Optional PRID identifier */
    prid?: string;
    /** Website configuration */
    website?: {
        /** Whether to remove Symphony branding from the website */
        removeSymphonyBrand?: boolean;
        /** Style configuration for the website */
        style?: WebsiteStyle;
        /** Form fields for data collection */
        fields?: Field[];
        /** Tour dates array */
        tourDates?: TourDate[];
        /** Whether to hide past tour dates */
        hidePastTourDates?: boolean;
    };
    /** Spotify release type */
    spotify_release_type: string | null;

    // Record/Project specific fields
    /** Whether content is explicit (for records/projects) */
    explicit?: boolean;
    /** Release date (for records/projects) */
    release_date?: string;
    /** Total number of tracks */
    total_tracks?: number;
    /** Track metadata */
    tracks?: Track[];
    /** External IDs */
    external_ids: {
        /** ISRC code */
        isrc?: string;
        /** UPC code */
        upc?: string;
    } | Record<string, unknown>;
    /** Presave configuration (for records/projects) */
    presave?: {
        /** When content was saved */
        saved_at?: string;
        /** Style configuration */
        style: WebsiteStyle;
        /** Boost settings */
        boosts: {
            /** Enable automatic presaves */
            auto_presaves: boolean;
            /** Enable boost follows */
            boost_follows: boolean;
            /** Enable email collection */
            collect_emails: boolean;
        };
        /** Presave options */
        options: {
            /** Timezone setting */
            timezone: string;
            /** URL to redirect to */
            redirect_url: string;
            /** Whether to redirect to custom webpage */
            redirect_to_custom_webpage: boolean;
        };
        /** Presave subtitle */
        subtitle: string;
        /** Enabled platforms */
        platforms: string[];
        /** Release URL with UPC/ISRC */
        release_url_upc_isrc: string;
    };

    // Playlist specific fields
    /** Owner URL (for playlists) */
    owner_url?: string;
    /** Owner ID (for playlists) */
    owner_id?: string;
    /** Number of followers (for playlists) */
    followers?: number;
    /** Playlist description */
    description?: string;
}
export interface BrandContent {
    brand_id: number;
    full_info?: boolean;
    primary_customization?: {
        bgType: string;
        bgColor: string;
        buttonConfig: string;
        primaryColor: string;
        secondaryColor: string;
    };
    brand_preset?: {
        enableSocialIcons?: boolean;
        socialIcons: WebsiteSocialMediaIcon[];
        removeSymphonyBrand?: boolean;
        addAffiliateLinkToLogo?: boolean;
        selectedSymphonyLogoStyle?: SymphonyLogoStyleKey;
    }
    campaign_id?: number | string;
    content_metadata: ContentMetadata;
    id: number;
    name: string;
    slug: string;
    type: string;
    url: string;
    updatedAt: string;
    createdAt: string;
    linked_campaigns?: Array<CreatedCampaign>;

    prid?: string | undefined;
}

export interface ExternalLink {
    url: string;
    name: string;
    enabled?: boolean;
    isValidUrl?: boolean;
    type?: string;
    platform?: StreamingPlatform;
}

export interface Field {
    id: string,
    label: string,
    priority: number,
    required: boolean,
    reserved: boolean,
}

export interface SpotifyGrowth {
    growthData: GrowthData;
}

export interface EmailGrowth {
    viewsGrowth: GrowthData;
}

export enum ConnectorIndicatorTypes {
    SPOTIFY_FOLLOWERS = "SPOTIFY_FOLLOWERS",
    SPOTIFY_PLAYLIST_FOLLOWERS = "SPOTIFY_PLAYLIST_FOLLOWERS",
    SPOTIFY_STREAMS = "SPOTIFY_STREAMS",
    INSTAGRAM_FOLLOWERS = "INSTAGRAM_FOLLOWERS",
    SHAZAMS = "SHAZAMS",
    INSTAGRAM_POST_REACH = "INSTAGRAM_POST_REACH",
    INSTAGRAM_POST_TOTAL_INTERACTIONS = "INSTAGRAM_POST_TOTAL_INTERACTIONS",
    INSTAGRAM_PROFILE_REACH = "INSTAGRAM_PROFILE_REACH",
    INSTAGRAM_PROFILE_VISITS = "INSTAGRAM_PROFILE_VISITS",
    NONE = "NONE",
}

export enum Platforms {
    followers = "followers",
    streams = "streams",
}

export enum RecordType {
    record = "record",
    project = "project",
}

export type ShazamConnectionData = {
    url: string;
    name: string;
};

export type FollowersChartData = {
    datetime: string;
    fanbase_count: string;
    platform: string;
    platform_identifier: string;
    percentageGrowth: number;
};

export type StreamsChartData = {
    datetime: string;
    platform: string;
    platform_identifier: string;
    views_stats: number | null;
    comments_stats: number | null;
    likes_stats: number | null;
    views_change: number | null;
};

export type GrowthData = {
    currentGrowthValue: string;
    difference: number;
    percentage: number;
};

export type ViewsGrowth = GrowthData;

/**
 * Used when referring to RecordSelector objects (either a single record, a project, or a playlist)
 */
export type RecordSelectorType = 'single' | 'project' | 'playlist'

export type FollowersData = {
    platform: string;
    stat: string;
    chartData: FollowersChartData[];
    growthData: GrowthData;
};

export type StreamsData = {
    data: StreamsChartData[];
    viewsGrowth: ViewsGrowth;
    platform: string;
    stat: string;
};

export enum TooltipSubtitles {
    Followers = "Followers",
    Streams = "Streams",
    Shazams = "Shazams",
}

export interface ImpactChartData {
    type: ConnectorIndicatorTypes;
    day: string;
    count: number;
    date: string;
    campaignStartDate: string;
    valueOf: number;
}

export enum AutoCompleteCloseReasons {
    toggleInput = "toggleInput",
    escape = "escape",
    selectOption = "select-option",
    blur = "blur",
    removeOption = "remove-option",
}

export enum AutoCompleteChangeReasons {
    createOption = "create-option",
    selectOption = "select-option",
    removeOption = "remove-option",
    clear = "clear",
    blur = "blur",
}

export interface WebsiteSocialMediaIcon {
    enabled: boolean;
    rounded: boolean;
    backgroundColor: string;
    iconColor?: string;
    height: string;
    width: string;
    url: string;
    key: string;
    placeholder: string;
}

export type PresaveWebsiteStyle = {
    bgType: string;
    primaryColor: string;
    buttonConfig: string;
    secondaryColor: string;
    bgColor: string;
    removeSymphonyBrand?: boolean;
    enableSocialIcons?: boolean;
    socialIcons?: WebsiteSocialMediaIcon[]
    titleAlignment?: TitleAlignments;
} | null;


// Types for Connections
export interface FacebookPageType {
    access_token: string;
    url: string; // NOTE CONNER: not sure if we have this
    category: string;
    followers_count: number;
    id: string;
    name: string;
    picture: { data: { url: string } };
    /** Whether the Facebook page is published and live */
    is_published?: boolean;
    connected_instagram_account?: ConnectedInstagramAccountType;
    instagram_business_account?: InstagramBusinessAccountType;
}

export interface InstagramPageType {
    id: string;
    name: string;
    businessAccount: InstagramBusinessAccountType | null;
    url: string;
    instagramAccount: {
        follow_count: number;
        followed_by_count: number;
        id: string;
        profile_picture_url: string;
        username: string;
    };
    connected_instagram_account?: ConnectedInstagramAccountType | null
}

export interface AdAccountPageType {
    account_status: number;
    amount_spent: number;
    id: string;
    name: string;
    business: {
        id: string;
        name: string;
        permitted_roles: string[];
        profile_picture_uri: string;
    }
    userHasAccess?: boolean;
    currency: string;
    spend_cap: number;

    // Enhanced validation properties (computed during getFacebookAdAccounts)
    /** Array of missing permissions (e.g., ['ANALYZE', 'ADVERTISE']) */
    missingPermissions?: string[];
    /** Whether the account status is valid for advertising */
    hasValidAccountStatus?: boolean;
    /** Whether the account has a valid funding source (payment method) */
    hasFundingSource?: boolean;
    /** Whether basic terms of service have been accepted */
    hasBasicTosAcceptance?: boolean;
    /** Whether the account is fully valid and ready for advertising */
    isFullyValid?: boolean;
    /** Human-readable error message if account has issues */
    errorMessage?: string | null;
}

/** Stats data point representing an event and its count */
interface PixelStatData {
    /** Name of the event */
    value: string;
    /** Number of times the event occurred */
    count: number;
}

/** Stats data structure for pixel events */
interface PixelStats {
    /** Array of stats data points */
    data: {
        data: PixelStatData[];
    }[];
}

/** Facebook Pixel data structure */
export interface FBPixel {
    /** Unique identifier of the pixel */
    id: string;
    /** Name of the pixel */
    name: string;
    /** Timestamp when pixel was created */
    creation_time: string;
    /** Business that owns the pixel */
    owner_business: {
        id: string;
        name: string;
    };
    /** Whether the pixel is unavailable */
    is_unavailable?: boolean;
    /** Optional owner ad account details */
    owner_ad_account?: {
        id: string;
        name: string;
        account_id: string;
    };
    /** Pixel statistics data */
    stats?: PixelStats;
}

export interface TikTokAdAccount {
    advertiser_id: string;
    advertiser_account_type: string;
    name: string;
    status: string;
    currency: string;
    timezone: string;
    display_timezone: string;
    owner_bc_id: string;

    // Optional fields
    address?: string;
    company?: string;
    contacter?: string;
    country?: string;
    description?: string;
    email?: string;
    industry?: string;
    language?: string;
    license_no?: string;
    license_url?: string;
    cellphone_number?: string;
    rejection_reason?: string;
    role?: string;
    telephone_number?: string;
    balance?: number;
    create_time?: Date;
    accountStatusValid?: boolean;
    userHasAccess?: boolean;
    amount_spent?: number;
    license_city?: string;
    license_province?: string;
    promotion_area?: string;
    promotion_center_city?: string;
    promotion_center_province?: string;
    advertiser_name?: string;
    brand?: string | null;
}

export interface TikTokArtist {
    url: string;
    username: string;
    picture?: string;
}

export interface TikTokBusinessProfile {
    email: string;
    avatar_url: string;
    create_time: number;
    core_user_id: string;
    display_name: string;
}

export interface TikTokBusinessCenter {
    bc_info: {
        name: string;
        type: string;
        bc_id: string;
        status: string;
        company: string;
        currency: string;
        timezone: string;
        registered_area: string;
        organization_type: string;
    };
    user_role: 'STANDARD' | 'ADMIN' | string;
    ext_user_role: {
        finance_role: string | null;
    };
}

export interface TikTokLoggedUser {
    connected: boolean;
    access_token?: string;
    tiktok_user?: TikTokBusinessProfile;
    tiktok_ad_account?: TikTokAdAccount;
}

export interface Connections {
    bandsintown?: {
        id: string;
        url: string;
        name: string;
        image_url: string;
        thumb_url: string;
        tracker_count: number;
        upcoming_event_count: number;
        links: Array<{
            url: string;
            type: string;
        }>;
    };
    soundcloud?: Record<string, unknown>;
    chartmetric: { id: number };
    deezer?: DeezerArtistProfile;
    tiktok_auth?: TikTokLoggedUser;
    tiktok_ad_account_funded?: boolean;
    tiktok_user?: TikTokBusinessProfile;
    tiktok_ad_account?: TikTokAdAccount;
    tiktok_business?: {
        connected: boolean;
        access_token?: string;
    }
    facebook_ad_account: AdAccountPageType;
    facebook_business_manager: {
        id: string;
        name: string;
        permitted_roles: string[];
        profile_picture_uri: string;
    };
    facebook_page: FacebookPageType;
    facebook_pixel: FBPixel;
    /** Website pixel IDs for manual website tracking - separate from ads facebook_pixel */
    website_facebook_pixels?: string[];
    instagram_page: InstagramPageType;
    business_discovery_instagram: {
        id: string;
        username: string;
        profile_picture_url: string;
        picture?: string;
        followers_count: number;
    };
    reauthenticateFbUser: boolean;
    spotify_artist_page: {
        thumbnail_url?: string;
        external_urls: {
            spotify: string;
        };
        followers: {
            href: string | null;
            total: number;
        };
        genres: any[];
        href: string | null;
        id: string;
        images: {
            height: number;
            width: number;
            url: string;
        }[];
        name: string;
        popularity: number;
        type: string;
        uri: string;
    };
    tiktok: TikTokArtist | null;
    tiktok_pixel?: string;
    google_analytics?: string;
    youtube_channel: YouTubeChannel;
    audiomack: {
        url?: string;
        username?: string;
        id?: string;
        name?: string;
        genres?: string[];
        followers?: number;
        thumbnail_url?: string;
    }
    twitter: {
        url: string;
        name: string;
        screen_name: string;
        connectedAt?: Date;
    };
    soundcloud_artist_page: SoundcloudArtistProfile;
    logged_in_fb_user?: LoggedUser
    facebook_pixel_conversions_api_access_token?: string | null

    // New platform connections
    shopify?: { url: string; connectedAt?: Date; };
    etsy?: { url: string; connectedAt?: Date; };
    newsletter?: { url: string; connectedAt?: Date; };
    website?: { url: string; connectedAt?: Date; };
}

export interface LoggedUser {
    id?: string;
    name?: string;
    picture?: { data: { url: string } };
    access_token: string;
    expires_at?: string | null;
    fbTokenValid: boolean;
}

export type FacebookAdAccount = {
    id: string;
    name: string;
    account_status: AdAccountStatusCodes;
    amount_spent: number;
    user_tasks?: string[];
    userHasAccess?: boolean;
    business?: {
        id: string,
        name: string
    },
    currency: string;
}

export type FacebookBusinessManager = {
    id: string;
    name: string;
    permitted_roles: string[];
    profile_picture_uri: string;
}

export type SubscriptionTier = 'pro' | 'free' | 'organization' | null;
export type SubscriptionType = 'organic' | 'forced' | 'referred' | null;
export type SubscriptionStatus = 'active' | 'trialing' | 'canceled' | 'unpaid' | 'incomplete' | 'past_due' | null;

export interface BrandSubscriptionDetails {
    freeTrialOver: boolean;
    liteFreeTrialOver: boolean;
    is_trial?: boolean;
    payment_method: string;
    recurring_interval: string;
    tier: SubscriptionTier;
    type: SubscriptionType;
    status: SubscriptionStatus;
    onOldPlan?: boolean;
    allowCustomerPortal?: boolean;
    canceled_at?: string;
    // stripe customer id
    cid?: string;

    // this is set to true when a subscription goes from past_due -> unpaid
    //  we hide the payment banner at this point in time
    retriesFailed?: boolean;
    details: {
        subscribed: boolean,
        forced: boolean,
        trialDaysLeft: number | null,
        trialOver: boolean | null
        viewSubscriptionUrl?: string | null
    } | null;

    // for when a user is downgrading from Pro to Free for the first time
    coupon_on_cancelation_redeemed?: boolean | null;
}

export type FacebookError = {
    code: string;
    name: string;
    details: string | { url: string; };
    title?: string
    description?: string
    ctaText?: string
    ctaLink?: string
    ctaAction?: string
    supportMessage?: string
    intercomArticleId?: string
    showToUsers?: boolean;
    expirationDate?: string;

}

/**
 * Represents the current brand/artist profile in the application
 * Contains core information about the brand, its connections, and configuration
 */
export interface CurrentBrand {
    isFirstSession?: boolean;
    /** Unique identifier for the brand */
    id: number;

    /** All third-party platform connections (Facebook, Instagram, Spotify etc.) */
    connections: Connections;

    /** Details about the brand's subscription status and plan */
    subscription?: BrandSubscriptionDetails;

    /** Flag indicating if brand's content has been initialized */
    content_initialized: boolean;

    /** Flag indicating if brand's dashboard has been initialized */
    dashboard_initialized: boolean;

    /** URL to brand's profile image/logo */
    image: string | null;

    /** Display name of the brand/artist */
    name: string;

    /** URL-friendly version of the brand name used in routes */
    slug: string;

    /** Website configuration and styling preferences */
    website: PresaveWebsiteStyle;

    /** Subscription tier level (e.g. 'pro', 'free', 'organization') */
    tier: PlanType;

    /** Indicates if brand's free trial period has ended */
    freeTrialOver?: boolean | null;

    /** Controls visibility of the Fans tab in navigation */
    hideFansTab?: boolean | null;

    /** Status of Facebook Conversions API setup and configuration */
    conversionsStatus?: ConversionsStatus;

    /** Details about brand's affiliate program participation */
    affiliate_details?: AffiliateDetails;

    /** Brand's preferred currency for transactions and reporting */
    currency?: BrandCurrency;

    /** Controls visibility of recommendation features */
    showRecommendations?: boolean;

    /** User's permission level for this brand (admin, member etc.) */
    permissionLevel: UserPermissionOnBrand;


    /** From Admin Search Only: Indicates if brand was found through admin search functionality */
    isFromAdminSearch?: boolean;

    organizationId?: number;
    organizationName?: string;

    /** Admins have access to users via admin search */
    users?: BrandUser[];

    /** Admins have access to createdAt date of when the brand was created */
    createdAt?: string;
    /** For Team Plan, access to fanbaseSize of the brand. Only avail for brands with organizations */
    fanbaseSize?: number
    /** For Team Plan, access to totalCampaigns of the brand. Only avail for brands with organizations */
    totalCampaigns?: number;
    /** Whether the brand is on a grandfathered plan */
    isGrandfathered?: boolean;
}

export enum ConversionsTask {
    AD_ACCOUNT_CONNECTED = 'AD_ACCOUNT_CONNECTED',
    FB_PIXEL_CONNECTED = 'FB_PIXEL_CONNECTED',
    CONVERSIONS_ACCESS_TOKEN = 'CONVERSIONS_ACCESS_TOKEN',
}

export interface ConversionsStatus {
    conversions_tasks: ConversionsTask[],
    conversions_tasks_completed: boolean,
    isEventRegistered: boolean,
    isError?: boolean,
    timesVerified?: number,
    showFinishedModal?: boolean,
}

export interface PreSave {
    platforms: string[];
    style: PresaveWebsiteStyle,
    boosts: {
        auto_presaves: boolean;
        boost_follows: boolean;
        collect_emails: boolean;
    };
    options: {
        redirect_url: string;
        redirect_to_custom_webpage: boolean;
        timezone?: string;
    };
    release_url_upc_isrc: string;
    created_with_upc?: boolean;
    subtitle?: string | null;
    local_release?: boolean;
    hideReleaseDate?: boolean;
}

export interface CampaignMetadata {
    link: string;
    error?: {
        id: string;
        name: string;
        title: string;
        ctaLink?: string;
        ctaText?: string;
        ctaAction?: string;
        canRetry?: boolean; // NOTE: userInterventionNotNeeded is the variable name on the frontend
        description?: string;
        isInternal?: boolean;
        expirationDate?: string; // When to stop showing the error (48 hours after campaign start)
    }
    budget: string;
    playbook: PlaybookType;
    campaign_platforms: string[];
    campaign_type: CampaignType | PlaybookType;
    presave?: PreSave;
    contentType?: string;
    v2_campaign_input?: FacebookCampaignSubmissionType | TikTokCampaignSubmissionType | YouTubeCampaignSubmissionType;
    content: {
        subject_line?: string;
        campaign_name?: string;
        id?: number;
        name: string;
        slug: string;
        url: string;
        thumbnail_url: string;
        type: string;
        caption?: string;
        link?: string;
    };
    endDate: string;
    platform_details: {
        ad_account: string;
        ad_types: {
            name: string;
            ad_ids: string[];
        }[];
        access_token: string;
        campaigns: {
            id: string;
            name: string;
            geographyId: number;
            campaignParams: {
                name: string;
                status: string;
                objective: string;
                adAccountId: string;
                pacing_type: string[];
                access_token: string;
                lifetime_budget?: number;
            };
        }[];
        campaignsMapping: CampaignMapping[];
    };
    creative?: any;
    startDate: string;
    status: string;
    status_notes: any;
    maestroGenerationTime: string;
    campaign_state?: {
        campaign?: {
            conversions?: {
                conversionsApiAccessToken: string;
                conversionsDomain: string;
                conversionsEventName: string;
                conversionsEnabled: boolean;
            }
        },
        brand?: {
            connections?: Connections
        }
    }
    maestro_recommendations?: {
        generationTime?: string;
    }
    friendlyName?: string;
    // Auto-fix tracking fields
    last_error_at?: string;
    mediaWidthFixApplied?: boolean;
    mediaWidthFixedAt?: string;
}


export interface CampaignMapping {
    countries: string[];
    excluded_countries: string[];
    fbCampaign: {
        id: string;
        adsets: unknown[];
        fields: {
            bid_strategy: string;
            lifetime_budget: number;
            name: string;
            objective: string;
            pacing_type: string[];
            special_ad_categories: string;
            status: string;
        };
    };
    id: string;
    fraction: number;
    name: string;
}

//TODO: Check for creative object brands types, adsets and status_notes
export interface CampaignDetails {
    brand_id: number;
    createdAt: string;
    id: number;

    campaign_name?: string;

    totalOfEmails?: number;
    totalOfPresaves?: number;
    emailsData?: {
        new: number;
        total: number;
    }
    presavesData?: {
        new: number;
        total: number;
    }
    foreverSavesData?: {
        new: number;
        total: number;
    }
    newFollowersData?: {
        new?: number;
        total: number;
    }

    status: string;
    campaign_metadata: CampaignMetadata;
    totalOfForeverPresaves?: number;
    analytics?: any;
}

/** Interface for playlisting campaign specific metadata */
export interface PlaylistingCampaignMetadata extends CampaignMetadata {
    /** Price and tier information for the playlisting campaign */
    price_data: {
        /** Target follower tier for the campaign */
        tier: string;
        /** Campaign price */
        price: number;
        /** Currency code */
        currency: string;
    };
    /** Currency information */
    currency: {
        /** Currency code (e.g. 'USD') */
        code: string;
    };
    /** Reason for campaign denial if applicable */
    denied_reason?: string;
    /** Current state of playlists */
    playlists_state?: {
        /** Total followers across all accepted playlists */
        total_followers?: number;
        /** Number of active playlists */
        active_playlists?: number;
    };
}



/** Interface for Facebook creative object */
interface FacebookCreative {
    /** URL of the media asset */
    media_url?: string;
    /** Owner/page that posted the content */
    owner?: string;
    /** Array of all media assets */
    all?: Array<{
        url: string;
        thumbnail?: string;
    }>;
    /** Type of media (VIDEO, IMAGE, etc) */
    media_type?: string;
    /** Shareable preview link */
    preview_shareable_link?: string;
    /** Caption for the creative */
    caption?: string;
    /** Thumbnail URL */
    thumbnail_url?: string;
}

/** Interface for platform details specific to Facebook campaigns */
interface FacebookPlatformDetails {
    /** Facebook access token */
    access_token: string;
    /** Ad account ID */
    ad_account: string;
    /** Array of campaign IDs */
    campaigns: string[];
    /** Mapping of campaigns to their details */
    campaignsMapping?: any;
    /** Array of ad types and their IDs */
    ad_types: Array<{
        name: string;
        ad_ids: string[];
        friendlyName?: string;
    }>;
}


export interface CreatedCampaign {
    brand_id: number;
    campaign_metadata: CampaignDetails['campaign_metadata'];
    createdAt: CampaignDetails['createdAt'];
    id: CampaignDetails['id'];
    status: CampaignDetails['status'];
}

export type Template = {
    name: string;
    templateId: string;
    previewUrl: string;
    timeToRenderSecs: number;
};

export type ColorPalette = {
    color1: string;
    color2: string;
    color3: string;
};

export interface JobDetails {
    jobId: string;
    status: string;
    referenceId: string;
    progress?: number | null;
    estimatedTimeToComplete?: number | null;
    links: Array<{ key: string; type: string }>;
}

export interface CreativeCampaign {
    id: number;
    status: string;
    campaign_type: string;
    content: {
        name: string;
        slug: string;
        url: string;
        thumbnail_url: string;
    };
    payment_method?: string | null;
    creativeData: {
        template: Template;
        colorPalette: ColorPalette;
    };
    jobDetails: JobDetails;
}

export type PlaylistData = {
    playlistName: string;
    playlist_followers: number;
    playlist_id: string;
};

export type PlaylistDatav2 = {
    playlistName: string;
    playlistFollowers: number;

    playlistStatus?: string;
    playlistUrl?: string;
    playlistPosition?: number;
    totalTracks?: number;
    playlistImages?: {
        url: string;
        width: string;
        height: string;
    }
    addedAt?: string;
    remainingDays?: number;
    estimatedRemovalDate?: string;
};

export enum CampaignCreationErrorCodes {
    FB_ASPECT_RATIO_NOT_SUPPORTED = "FB_ASPECT_RATIO_NOT_SUPPORTED",
    FB_ACCESS_TOKEN_INVALID = "FB_ACCESS_TOKEN_INVALID",
    FB_ASSETS_NOT_FOUND = "FB_ASSETS_NOT_FOUND",
    FB_VIDEO_STILL_PROCESSING = "FB_VIDEO_STILL_PROCESSING",
    FB_BUDGET_TOO_LOW = "FB_BUDGET_TOO_LOW",
    FB_ADACCOUNT_ISSUE = "FB_ADACCOUNT_ISSUE",
    FB_UNKNOWN_ERROR = "FB_UNKNOWN_ERROR",
    FB_TOO_MANY_CALLS = "FB_TOO_MANY_CALLS",
    FB_RECORD_NOT_FOUND = "FB_RECORD_NOT_FOUND",
    FB_VIDEO_INELIGIBLE_TO_BOOST = "FB_VIDEO_INELIGIBLE_TO_BOOST",
}

export enum CampaingCreationErrorSuggestions {
    REFRESH_PAGE = "REFRESH_PAGE",
    RETURN_TO_CREATIVES_PAGE = "RETURN_TO_CREATIVES_PAGE",
    WAIT = "WAIT",
    RETURN_TO_BUDGET_PAGE = "RETURN_TO_BUDGET_PAGE",
    FIX_ON_FACEBOOK = "FIX_ON_FACEBOOK",
    CONTACT_SUPPORT = "CONTACT_SUPPORT",
}

export type CampaignCreationError = {
    data?: {
        error: {
            title?: string;
            code?: string;
            message?: string;
            suggestion?: string;
            canBeRetried?: boolean;
            link?: string;
        };
    } | null;
    message?: string | null;
    isError?: boolean | null;
};

export type FBCustomAudience = {
    name: string,
    id: string,
    rule: string,
    rule_aggregation: string,
    description: string,
    delivery_status: {
        code: number,
        description: string
    },
    lookalike_spec: Object,
    data_source: Object,
    subtype: string,
    approximate_count_lower_bound: number,
    approximate_count_upper_bound: number,
    is_value_based: boolean,
    opt_out_link: string
};

export type FbSuggestion = {
    id: string;
    name: string;
    audience_size: number | null;
    country: string | null;
    country_access: string | null;
    description: string | null;
    partner: string | null;
    path: string[] | null;
    source: string | null;
    topic: string | null;
    type: string | null;
    artistId: string | null;
};

export interface TikTokTargetingGeography {
    id: string;
    name: string;
    label: string;
    parent_name: string | null;
    parent_id: string | null;
    area_type: string;
    region_level?: string;
    region_code?: string;
    support_below_18?: boolean;
    next_level_ids?: string[];
}

export type TargetedLanguage = {
    key: number | string;
    name: string;
}


export enum GENDERS {
    MALE = 'Men',
    FEMALE = 'Women'
}

export type TargetedGender = {
    key: number | string;
    name: GENDERS;
}

export type AgeRange = {
    min: number,
    max: number,
}

export type DemographicsTargetingType = {
    age: AgeRange,
    gender: string,
    languages: Array<TargetedLanguage>
}

export type FbSuggestionsResponse =
    | {
        data: FbSuggestion[];
        paging: {
            cursors: {
                after: string;
                before: string;
            };
        };
    }
    | null
    | undefined;

export type SuggestedAudience = {
    name: string;
    fbId: string;
};

export type DataFBQueryAdInterest = {
    id: string;
    name: string;
    audience_size_lower_bound?: number | null;
    audience_size_upper_bound?: number | null;
    audience_size?: number | null;
    path: string[] | null;
    description: string | null;
    topic: string | null;
    valid?: boolean | null;
}[];

export type FacebookInterestSearchData = {
    interests: DataFBQueryAdInterest;
    reasoning: string;
}

export type OverallCampaignStatusType = {
    status: OverallCampaignStatus;
    note: JSX.Element | string | null;
}

export type StreamingPlatformsData = {
    serviceName: string;
    result: number;
}[]

export enum OverallCampaignStatus {
    ERROR = "error",
    TO_RETRY = "to_retry",
    LIVE_WITH_ISSUES = "live_with_issues",
    ACTIVE = "active",
    LIVE_WITH_NO_STATS = "live_with_no_stats",
    IN_REVIEW = "in_review",
    IN_REVIEW_AFTER_ACTIVE = "in_review_after_active",
    DENIED = "denied",
    PAUSED = "paused",
    PENDING = "pending",
    ANTICIPATED_PAUSE = 'anticipated_puase',
    DEFAULT_OVERALL_CAMPAIGN_STATUS = PENDING,
    PROCESSING = 'processing',
}

export const OVERALL_CAMPAIGN_STATUS_KEYS = {
    ERROR: 'ERROR',
    TO_RETRY: 'TO_RETRY',
    LIVE_WITH_ISSUES: 'LIVE_WITH_ISSUES',
    ACTIVE: 'ACTIVE',
    LIVE_WITH_NO_STATS: 'LIVE_WITH_NO_STATS',
    IN_REVIEW: 'IN_REVIEW',
    IN_REVIEW_AFTER_ACTIVE: 'IN_REVIEW_AFTER_ACTIVE',
    DENIED: 'DENIED',
    PAUSED: 'PAUSED',
    PENDING: 'PENDING',
    ANTICIPATED_PAUSE: 'ANTICIPATED_PAUSE',
    DEFAULT_OVERALL_CAMPAIGN_STATUS: 'DEFAULT_OVERALL_CAMPAIGN_STATUS',
    PROCESSING: 'PROCESSING',
} as const;

export type OverallCampaignStatusKey = keyof typeof OVERALL_CAMPAIGN_STATUS_KEYS;

export type AdCampaignStatus = {
    id: string;
    name: string;
    effective_status: string;
};

/**
 * Represents a selected record in CreativeGenerator
 * + in WebsiteContext
 */
export interface RecordSelected {
    id: number;
    preview_url: string;
    explicit: boolean;
    external_links: { url: string; name: string }[];
    full_info: boolean;
    name: string;
    primary_artist: string;
    record_url: string;
    release_date: string;
    slug: string;
    url: string;
    spotify_id: string;
    spotify_release_type: string;
    spotify_uri: string;
    thumbnail_url: string;
    total_tracks: number;
    tracks?: Track[];
    type: string;
}

/**
 * Represents a track in a DuplicateCampaignModal
 */
export interface Track {
    explicit: boolean;
    name: string;
    record_url: string;
    spotify_id: string;
    spotify_uri: string;
    preview_url?: string;

    added_at?: string;
    artist_names?: string[];
    album_name?: string;
    id?: string;
    uri?: string;
    is_playable?: boolean;

}

export interface RecordPreviewData {
    options: { url?: string; name: string; show: boolean }[];
    name: string;
    artworkUrl: string;
    subtitle?: string;
}

export enum BoxContrasts {
    LIGHT = "light",
    DARK = "dark",
}

export enum BackgroundTypes {
    ARTWORK = "artwork",
    COLOR = "color",
}

export enum StyleTypes {
    BOXES = "boxes",
    STACKED = "stacked",
    TRADITIONAL = "traditional",
}

export enum TitleAlignments {
    LEFT = "left",
    CENTER = "center",
    RIGHT = "right",
}

export interface CampaignPreSaveCreationData {
    id: number;
    brand_id: number;
    status: string;
    campaign_metadata: {
        link: string;
        status: string;
        content: {
            name: string;
            slug: string;
            url: string;
            type: string;
            thumbnail_url: string;
        };
        endDate: string;
        presave: {
            style: {
                bgType: string;
                bgColor: string;
                buttonConfig: string;
                primaryColor: string;
                secondaryColor: string;
            };
            boosts: {
                auto_presaves: boolean;
                boost_follows: boolean;
                collect_emails: boolean;
            };
            options: {
                redirect_url: string;
                redirect_to_custom_webpage: boolean;
                timezone?: string;
            };
            platforms: string[];
            local_release?: boolean;
        };
        startDate: string;
        campaign_type: string;
    };
    updatedAt: string;
    createdAt: string;
}

export interface PreSaveCampaignDetails extends CampaignPreSaveCreationData {
    totalOfEmails?: number;
    totalOfPresaves?: number;
    emailsData?: {
        new: number;
        total: number;
    }
    presavesData?: {
        new: number;
        total: number;
    }
    foreverSavesData?: {
        new: number;
        total: number;
    }
}

export interface BrandContentExistsData {
    alreadyExists: boolean;
    brandContent: BrandContent;
    link: string;
}

export interface PresaveCampaignExistsData {
    alreadyExists: boolean;
    campaign: CampaignPreSaveCreationData;
}



export type FanbaseItem = {
    id: number;
    email: string;
    name: string;
    profile_url: string;
    lastname?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    platform?: string | null;
    tier?: string | null;
    color?: string;
    origin: string;
    song_name?: string | null;
    createdAt?: string | null;
};

export enum EmailCampaignStatus {
    SUBMITTED = "SUBMITTED",
    SCHEDULED = "SCHEDULED",
}

export enum MessageDataContentKeys {
    TARGETS = "targets",
    OPENED_BY = "openedBy",
    OPENING_RATE = "openingRate",
    SENT_TO = "sentTo",
}

export interface EmailCampaignData {
    brand_id: number;
    campaign_metadata: {
        campaign_type: string;
        content: {
            subject_line: string;
        };
        email_fanbase: {
            openedBy: number;
            openingRate: number;
            sentTo: number;
        };
        release_date: string;
        timezone: string;
        status: string;
    };
    createdAt: string;
    id: number;
    status: string;
}

//TODO: Check if genre, follows and saves are an array of strings
export interface EmailCampaignDetailsData {
    brand_id: number;
    campaign_metadata: {
        campaign_type: string;
        content: {
            subject_line: string;
            html_content: string;
        };
        email_fanbase: {
            openAverage: number;
            openedBy: number;
            openingRate: number;
            sentTo: number;
            clicks: number;
            sentList: {
                id: number;
                name: string;
                lastname: string;
                genre: string[];
                email: string;
                cellphone: string | null;
                city: string;
                state: string;
                country: string;
                gender: string | null;
                profile_url?: string;
                ip_address: string | null;
                follows: string[];
                saves: string[];
                spotify_user_id: string | null;
                apple_music_user_id: string | null;
                deezer_user_id: string | null;
                spotify_user_refresh_token: string | null;
                apple_music_user_token: string | null;
                deezer_user_access_token: string | null;
            }[];
            openedList: {
                email: string;
                name: string;
                lastname: string;
                city: string;
                state: string;
                country: string;
                profile_url?: string | null;
            }[];
            clicksList: {
                email: string;
                name: string;
                lastname: string;
                city: string;
                state: string;
                country: string;
                profile_url?: string | null;
            }[];
        };
        release_date: string;
        status: string;
    };
    createdAt: string;
    id: number;
    status: string;
}

/**
 * Represents a song in a Song Ads campaign
 */
export interface SongAdsRecord {
    explicit: boolean;
    external_ids: Record<string, string>;
    external_links: { url: string; name: string }[];
    full_info: boolean;
    name: string;
    preview_url: string;
    primary_artist: string;
    record_url: string;
    release_date: string;
    slug: string;
    spotify_id: string;
    spotify_release_type: string;
    spotify_uri: string;
    thumbnail_url: string;
    type: string;
    updateCount: number;
    url: string | null;

    // this is only used if the user pastes in a spotify-provided
    // special URL. if so, then spotify provides a personalized playlist to the user.
    prid?: string

    // this is the primary track for a playlist campaign
    // to redirect to
    mainTrack?: Track


}

/**
 * Represents a duplicate song in a Song Ads campaign
 */
export interface DuplicateSongAdsRecord {
    full_info: boolean;
    external_links: { url: string; name: string }[] | null;
    primary_artist: string;
    name: string;
    thumbnail_url: string;
    parentName: string | null;
    explicit: boolean | null;
    record_url: string;
    spotify_id: string;
    spotify_uri: string;
    release_date: string;
    slug: string | null;
    url: string | null;
    type?: string;
    spotify_release_type: string;
    spotify_release_note: string;
    tracks?: {
        explicit: boolean;
        name: string;
        record_url: string;
        spotify_id: string;
        spotify_uri: string;
    }[]
    trackLength?: number
}

/**
 * Represents a track in a Get Playlist campaign
 */
export interface PlaylistingCampaignTrack {
    id?: string
    explicit: boolean;
    external_ids: Record<string, string>;
    external_links: { url: string; name: string }[];
    full_info: boolean;
    name: string;
    preview_url: string;
    primary_artist: string;
    record_url: string;
    release_date: string;
    slug: string;
    spotify_id: string;
    spotify_release_type: string;
    spotify_uri: string;
    thumbnail_url: string;
    type: string;
    url: string;
}

export interface VideoAdsRecord {
    channelId: string;
    channelTitle: string;
    description: string;
    id: string;
    liveBroadcastContent: string;
    name: string;
    publishTime: string;
    publishedAt: string;
    thumbnails: {
        defaults: {
            height: number;
            width: number;
            url: string;
        };
        medium: {
            height: number;
            width: number;
            url: string;
        };
        hight: {
            height: number;
            width: number;
            url: string;
        };
    };
    title: string;
    primaryThumbnail: string;
}

export interface UserData {
    id: number;
    userid: string;
    email: string;
    emailVerified: string;
    name: string;
    metadata?: UserMetadata; // The '?' denotes that metadata is optional
}

export enum OrganizationStatus {
    NOT_STARTED = "not_started",
    WAITING_FOR_DETAILS = "waiting_for_details",
    READY = "ready",
    UNPAID = "unpaid",
}

export enum OrganizationInvitationStatus {
    PENDING = 'pending',
    ACCEPTED = 'accepted',
    REJECTED = 'rejected'
}

/**
 * Interface representing organization data and status
 */
export interface OrganizationData {
    /** Unique identifier for the organization */
    id: number;
    /** Current status of the organization's setup process */
    setup_status: OrganizationStatus;
    /** Name of the organization */
    name: string,
    /** User's role within the organization */
    role: OrganizationRole;
    /** Array of brands associated with the organization */
    brands: CurrentBrand[]
    /** The ID of the organization owner */
    owner_id: number;
}

export interface OrganizationTeamData {
    organization: OrganizationData;
    organizationUsers: any[];
    organizationBrands: any[];
}

interface UsageMetrics {
    usage: number;
    limit: number;
    additionalCost: number;
}
export interface OrganizationBillingData {
    nextBillingDate: string;
    trialEndDate?: string;
    scheduledCancellationDate?: string | null;
    status: SubscriptionStatus;
    viewCustomerUrl: string;
    recurringInterval: 'monthly' | 'annual';
    viewSubscriptionUrl: string;

    baseSubscriptionPrice: number;

    usage: {
        adSpend: UsageMetrics;
        seats: UsageMetrics;
        proAccounts: UsageMetrics;
    };
}
export interface AffiliateDetails {
    discountAmount: number;
    partnerId: string;
    partnerName: string;
    cookieExpirationFromSignup: number;
    affiliate_referrer?: string;
    startDate: string;
    shownPlansOnSignup?: PlanType[];
    defaultRecurringIntervalOnSignup?: BillingCycle;
    planDiscounts?: {
        [PlanType.LITE]?: {
            [BillingCycle.MONTHLY]?: number;
            [BillingCycle.ANNUAL]?: number;
        },
        [PlanType.PRO]?: {
            [BillingCycle.MONTHLY]?: number;
            [BillingCycle.ANNUAL]?: number;
        }
    }
}

export interface AttributionDetails {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
}

export interface RewardfulDetails {
    rewardful_affiliate_id?: string;
    rewardful_via?: string;
}

export interface OrganizationSubscriptionStatus {
    status: string;
    stripe_session_id: string;
    stripe_customer_id: string;
    stripe_subscription_id: string;
    initialOnboardingCompleted: boolean;
    recurring_interval: string;
    organization_free_trial_redeemed?: boolean
    trial_start_date?: string
}

export interface UserMetadata {
    // used to track partners who drive to symphony
    affiliate_referrer: string;
    affiliate_details?: AffiliateDetails;
    affiliate_initiated?: string;
    affiliate_expiration?: string;

    // used to track referral attribution
    attribution?: AttributionDetails

    // used to track Last Touch Atrribution
    last_touch_attribution?: AttributionDetails

    // used to track when users are driven by a rewardful signup
    rewardful?: RewardfulDetails

    // used to track subscriptino organization status
    organization_subscription?: OrganizationSubscriptionStatus;
    // used to track when users have completed onboarding so we dont show it again
    onboarding_answers?: any

    // used to track when a user has explcitily created a free trial on their end
    free_trial_redeemed?: boolean
    lite_free_trial_redeemed?: boolean
    organization_free_trial_redeemed?: boolean
    trial_start_date?: Date
    // the last visited time based on accessed
    last_visited?: string;

    // rewardful details
    rewardful_affiliate_id?: string;
    rewardful_affiliate_link?: string;

    // organization
    hasTeamPlanAccess?: boolean;
}

export enum Breakpoints {
    sm = "sm",
    md = "md",
    lg = "lg",
    xl = "xl",
    xxl = "xxl",
}

export enum PreSaveErrorTypes {
    PRE_SAVE_CAMPAIGN_EXISTS = "PRE_SAVE_CAMPAIGN_EXISTS",
    BRAND_CONTENT_EXISTS = "BRAND_CONTENT_EXISTS",
    ERROR_ON_PRE_SAVE_CREATION = "ERROR_ON_PRE_SAVE_CREATION",
}

export enum FansMainGoals {
    PRE_SAVE = "PreSave",
    UPLOAD_EMAILS_BY_CSV = "UploadEmailsByCsv",
    UPLOAD_EMAILS_BY_PASTE = "UploadEmailsByPaste",
    MESSAGE_YOUR_FANS = "MessageYourFans",
}

export enum ScheduleOptions {
    ASAP = "ASAP",
    SCHEDULED = "SCHEDULED",
}

export enum ViewTypes {
    DESKTOP = "DESKTOP",
    MOBILE = "MOBILE",
}

export enum MessageYourFansTabs {
    DESIGN = "DESIGN",
    DETAILS = "DETAILS",
    FINALIZE = "FINALIZE",
}

export enum UploadEmailsByCsvTabs {
    UPLOAD_FILE = "UPLOAD_FILE",
    MATCH_FILE = "MATCH_FILE",
    FINALIZE = "FINALIZE",
}

export enum UploadEmailsByPasteTabs {
    PASTE_EMAILS = "PASTE_EMAILS",
    SOURCE = "SOURCE",
    FINALIZE = "FINALIZE",
}

export enum ImportFansStatus {
    IN_PROCESS = "IN_PROCESS",
    FINISHED = "FINISHED",
    DISRUPTED = "DISRUPTED",
}

export enum PaymentMethods {
    STRIPE = "stripe",
    PAYPAL = "paypal",
}

export type OnboardingArtist = {
    chartmetric: boolean
    cm_artist_score: unknown
    id: number | null
    slug: string | null
    image_url: string
    isni: unknown
    name: string
    sp_followers: number
    images: { url: string }[]
    followers: { total: number }
}

// NOTE: our data transform
export type SpotifyArtistInBrandConnections = {
    id: string // this is the same id as SpotifyArtistSearchResponse
    name: string
    images: { height: number; url: string; width: number }[]
    followers: {
        href: string | null
        total: number
    }
    externalUrls: { spotify: string }
    genres: string[]
    href: string
    popularity: number
    type: string
    uri: string
}

// NOTE: this comes straight from Spotify
export type SpotifyArtistSearchResponse = {
    id: string
    name: string
    thumbnail_url: string | null
    followers: number | null
    genres: string[]
}

export type AudiomackArtistProfile = {
    id: number | string;
    name: string;
    url_slug: string;
    thumbnail_url: string;
    bio: string;
    followers: number;
}

export type SoundcloudArtistProfile = {
    id: number | string;
    name: string;
    url_slug: string;
    thumbnail_url: string;
    tracks: number;
    permalink_url: string;
    followers: number;
    username: string;

    // old parameters - when it was just URL-based linking
    url: string;
    urlStructure?: string;
}

export type DeezerArtistProfile = {
    id: number | string;
    name: string;
    thumbnail_url: string;
    permalink_url: string;
}

export type YouTubeChannel = {
    searchUrl?: string
    id: {
        channelId: string
        kind: string
    }
    etag: string
    kind: string
    snippet: {
        customUrl?: string
        channelId: string
        channelTitle: string
        title: string
        description: string
        liveBroadcastContent: string
        publishTime: string
        thumbnails: {
            default: {
                url: string
            }
            medium: {
                url: string
            }
            high: {
                url: string
            }
        }
    }
}

export type TikTokProfileForOnboarding = {
    username: string;
    url: string;
    picture?: string;
}

export type InstagramProfileForOnboarding = {
    username: string;
    url: string;
    picture?: string;
}

export type AnySocialConnectProfile =
    | AudiomackArtistProfile
    | DeezerArtistProfile
    | SoundcloudArtistProfile
    | SpotifyArtistSearchResponse
    | YouTubeChannel
    | FacebookPageType
    | ConnectedInstagramAccountType
    | TikTokArtist
    | InstagramProfileForOnboarding
    | TikTokProfileForOnboarding

export enum CreativeTabConnectTypes {
    FACEBOOK_BUSINESS_MANAGER_CONNECT = "facebook-biz-mgr",
    FACEBOOK_AD_ACCOUNT_CONNECT = "facebook-ad-account",
    FACEBOOK_PIXEL_CONNECT = "facebook-pixel",
    FACEBOOK_CONNECT = "facebook",
    INSTAGRAM_CONNECT = "instagram",
}

export interface InstagramBusinessAccountType {
    id: string;
    username: string;
    followers_count: number;
    profile_picture_url: string;
}

export interface ConnectedInstagramAccountType {
    id: string;
    username: string;
    followers_count: number;
    profile_picture_url: string;
}

export type FacebookPageResponse = FacebookPageType
export interface ConnectorInstagramPagesType {
    connected_instagram_account: ConnectedInstagramAccountType | undefined,
    instagram_business_account: InstagramBusinessAccountType,
    facebookPage?: {
        name: string,
        id: string,
        category: string,
        access_token: string,
        followers_count: number,
        picture: string,
        is_published: boolean
    }
}

export enum SYMPHONY_TASKS {
    FANBASE_COMPLETED = "FANBASE_COMPLETED",
    CAMPAIGN_STARTED = "CAMPAIGN_STARTED",
    TEAM_INVITED = "TEAM_INVITED",
    WEBSITE_CUSTOMIZED = "WEBSITE_CUSTOMIZED",
    GROW_YOUTUBE_ENGAGEMENT = "GROW_YOUTUBE_ENGAGEMENT",
    INSTAGRAM_ENGAGEMENT = "INSTAGRAM_ENGAGEMENT",
}

export enum FANBASE_TASKS {
    PRESAVE_CREATED = "PRESAVE_CREATED",
}

export interface SymphonyTasks {
    symphony_tasks: SYMPHONY_TASKS[] | [],
    fanbase_tasks: FANBASE_TASKS[] | [],
    getting_started_completed: boolean,
    fanbase_tasks_completed: boolean,
}

export enum SavingDraftStatus {
    INITIAL_SAVING_STATE = 0,
    FIRST_TIME_DRAFT_DATA_LOAD = 1,
    SAVING_NEXT_DRAFT = 2
}

export interface Fan {
    id: number,
    name?: string,
    cellphone?: string,
    lastname?: string,
    email?: string,
    profile_url: string,
    city?: string,
    state?: string,
    country?: string,
    platform?: string,
    origin?: string,
    tier?: PlanType,
    presavedSongs: string[],
    createdAt: string,
    isNew: boolean,
    presaveFutureReleases: boolean,
    firstPresave?: string,
    newFollower?: boolean,
    newFollowerPlatformName?: string,
    updateSubscriptionLink?: string,
    source?: string,
}

export const THEME_BREAKPOINTS = {
    SMALL: 420,
    MOBILE: 768,
    MEDIUM: 1025,
    LARGE: 1200,
}

export const MEDIA_BREAKPOINTS = {
    smallView: `@media (max-width: ${THEME_BREAKPOINTS.SMALL - 1}px)`,
    mobileView: `@media (max-width: ${THEME_BREAKPOINTS.MOBILE - 1}px)`,
    mediumView: `@media (max-width: ${THEME_BREAKPOINTS.MEDIUM - 1}px)`,
    largeView: `@media (min-width: ${THEME_BREAKPOINTS.LARGE}px)`,
}

export interface VideoData {
    videoUrl: string;
    thumbnail: string;
    title: string;
    description?: string;
    titleModalOpened?: string;
    descriptionModalOpened?: string;
    intercomArticle?: () => void;
}

interface ConversionsVideos {
    onboardingVideo: VideoData
    stepVideos: {
        CONVERSIONS_ONBOARDING: VideoData
        AD_ACCOUNT_CONNECTED: VideoData
        FB_PIXEL_CONNECTED: VideoData
        CONVERSIONS_ACCESS_TOKEN: VideoData
        FB_PIXEL_NOT_CONNECTED: VideoData
        GETTING_STARTED: VideoData
        FB_PAGE: VideoData
        AD_ACCOUNT: VideoData
    }
}

//TODO: change to final conversions video urls
export const CONVERSIONS_VIDEOS: ConversionsVideos = {
    onboardingVideo: {
        videoUrl: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/videos/conversions.mp4",
        thumbnail: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/thumbnails/conversions.jpg",
        title: "Activate Symphony Conversions for 4-6x better results",
        description: "Setup Symphony's AI with Facebook's Conversions API to drive more cost-effective results.",
    },
    stepVideos: {
        CONVERSIONS_ONBOARDING: {
            videoUrl: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/videos/conversions.mp4",
            thumbnail: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/thumbnails/conversions.jpg",
            title: "Activate Symphony Conversions for 4-6x better results",
            description: "Setup Symphony's AI with Facebook's Conversions API to drive more cost-effective results.",
        },
        AD_ACCOUNT_CONNECTED: {
            videoUrl: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/videos/conversions.mp4",
            thumbnail: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/thumbnails/conversions.jpg",
            title: "How to grow faster with Symphony Conversions",
            intercomArticle: Intercom.OpenGrowConversionsArticle,
        },
        FB_PIXEL_CONNECTED: {
            videoUrl: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/videos/conversions.mp4",
            thumbnail: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/thumbnails/conversions.jpg",
            title: "What is a Pixel?",
            intercomArticle: Intercom.openWhatIsAPixelArticle,
        },
        CONVERSIONS_ACCESS_TOKEN: {
            videoUrl: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/videos/conversions_access_token.mp4",
            thumbnail: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/thumbnails/conversions-access-token.jpg",
            title: "Where to find your Conversion API Access Token",
            intercomArticle: Intercom.openWhereIsMyConversionsTokenArticle,
        },
        FB_PIXEL_NOT_CONNECTED: {
            videoUrl: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/videos/connect_pixel_to_ad_account.mp4",
            thumbnail: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/thumbnails/connect-pixel-to-ad-account.jpg",
            title: "How to connect your Pixel to your Ad Account",
            intercomArticle: Intercom.openConnectYourPixelToAdAccountArticle,
        },
        // TODO: replace this videos and copies with the final ones
        GETTING_STARTED: {
            videoUrl: "https://res.cloudinary.com/symphonyos/video/upload/w_800/v1729079713/internal-assets/Why_Connect_FB_Acct_ahymel.mp4",
            thumbnail: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/thumbnails/conversions.jpg",
            title: "Getting started",
            description: "Why link your Facebook and Instagram accounts and how it works?",
            titleModalOpened: "Getting started",
            descriptionModalOpened: "In this setup, we'll explain why linking your Facebook and Instagram accounts is crucial for running successful marketing campaigns on our platform. By the end of this setup, you'll understand the benefits of linking your accounts and be ready to proceed to run ads."
        },
        FB_PAGE: {
            // TODO: replace this video and thumbnail with the final ones
            videoUrl: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/videos/conversions.mp4",
            thumbnail: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/thumbnails/conversions.jpg",
            title: "Facebook page",
            description: "Why link your Facebook and Instagram accounts and how it works?",
            titleModalOpened: "Facebook page",
            descriptionModalOpened: "In this setup, we'll explain why linking your Facebook and Instagram accounts is crucial for running successful marketing campaigns on our platform. By the end of this setup, you'll understand the benefits of linking your accounts and be ready to proceed to run ads."
        },
        AD_ACCOUNT: {
            // TODO: replace this video and thumbnail with the final ones
            videoUrl: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/videos/conversions.mp4",
            thumbnail: "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/thumbnails/conversions.jpg",
            title: "Ad Account",
            description: "Why link your Facebook and Instagram accounts and how it works?",
            titleModalOpened: "Ad Account",
            descriptionModalOpened: "In this setup, we'll explain why linking your Facebook and Instagram accounts is crucial for running successful marketing campaigns on our platform. By the end of this setup, you'll understand the benefits of linking your accounts and be ready to proceed to run ads."
        },
    }
}

export type AdAccountStatusCodes = 1 | 2 | 3 | 7 | 9 | 101 | 100

export type AdAccountStatus = {
    name: string;
    valid: boolean;
}

export type AdAccountStatuses = {
    [key in AdAccountStatusCodes]: AdAccountStatus;
};

/**
 * Interface for tracking and managing spending limits, fees, and usage across Pro and Organization accounts.
 * Used throughout the application to display spending status, calculate budgets, and determine warning states.
 */
export interface SpendBudgetData {
    /**
     * Overall status of spending relative to limits
     * @property {"healthy"} healthy - Well below spending limits
     * @property {"warning"} warning - Approaching spending limit (typically 80%)
     * @property {"exceeded"} exceeded - Over the spending limit
     * @property {"exact"} exact - Exactly at the spending limit
     */
    status: "healthy" | "warning" | "exceeded" | "exact";

    // Spend tracking fields
    /** Current attempted spend amount */
    currentAttemptedSpend: number | null;
    /** Base monthly spending limit */
    monthlySpendCap: number;
    /** Total amount spent in current billing cycle */
    spentToDate: number;
    /** Remaining amount before hitting base limit */
    spentLeft: number;
    /** Total spend after current transaction */
    projectedSpendAfterCampaign: number | null;
    /** Adjusted limit including overage units */
    effectiveSpendLimit: number;

    // Fee calculation fields
    /** Total fees charged in current period */
    feeToDate: number;
    /** Fee Price for Pro Accounts (e.g., $20 for $1k in spend) */
    feePrice: number;
    /** Fee Percentage for Pro Accounts (e.g., 2% for $5k in spend) */
    feePercentage: number;
    /** Size of spending units for Pro accounts (default 1000) */
    feeUnit: number;
    /** Total fees for current transaction */
    feeTotal: number;
    /** Number of additional units being charged */
    feeUnitsToCharge?: number;

    /** Total charge including spend + fees */
    total: number | null;

    // Team/seats management
    /** Number of seats over the limit */
    seatsOverage: number | null;
    /** Maximum allowed seats */
    seatsLimit: number | null;
    /** Additional fee for extra seats */
    seatsOverFee: number | null;
    /** Current number of seats in use */
    totalSeatsUsed: number | null;

    // Important dates
    /** Date of next billing cycle */
    nextBillingDate?: string;
    /** Date subscription will be cancelled (if applicable) */
    scheduledCancellationDate?: string;
    /** Date trial period ends (if applicable) */
    trialEndDate?: string;
    /**
     * Current billing cycle dates
     * @property {string} startDate - Start of current billing period
     * @property {string} endDate - End of current billing period
     */
    billingPeriod?: {
        startDate: string;
        endDate: string;
    };

    /** Billing frequency */
    planType?: 'monthly' | 'annual';

    // Customer portal URLs
    /** URL to view/manage subscription */
    viewSubscriptionUrl?: string | null;
    /** URL to customer portal */
    viewCustomerUrl?: string | null;

    // Feature flags
    /** Whether spend limits are enforced */
    disableSpendLimit?: boolean;
    /** Whether this is the first spend */
    firstTimeSpending?: boolean;
    /** Whether account is organization type (affects fee calculation) */
    isOrganization?: boolean;

    // Additional metadata
    /** Currency for all monetary values */
    currencyCode?: string;
    /** Detailed usage metrics (if available) */
    usage?: UsageMetrics;

    // any active coupons
    coupons: Coupons[];
};

interface Coupons {
    amount_off: string;
    percent_off: string;
    duration: string;
    name: string
    start_date: number;
    end_date: number;
    promocode: string;
    valid: boolean;
};

export const CurrencyCodesTypeMapped = {
    AED: 'AED',
    AFN: 'AFN',
    ALL: 'ALL',
    AMD: 'AMD',
    ANG: 'ANG',
    AOA: 'AOA',
    ARS: 'ARS',
    AUD: 'AUD',
    AWG: 'AWG',
    AZN: 'AZN',
    BAM: 'BAM',
    BBD: 'BBD',
    BDT: 'BDT',
    BGN: 'BGN',
    BHD: 'BHD',
    BIF: 'BIF',
    BMD: 'BMD',
    BND: 'BND',
    BOB: 'BOB',
    BOV: 'BOV',
    BRL: 'BRL',
    BSD: 'BSD',
    // BTC: 'BTC',
    BTN: 'BTN',
    BWP: 'BWP',
    BYN: 'BYN',
    BYR: 'BYR',
    BZD: 'BZD',
    CAD: 'CAD',
    CDF: 'CDF',
    CHE: 'CHE',
    CHF: 'CHF',
    CHW: 'CHW',
    CLF: 'CLF',
    CLP: 'CLP',
    CNH: 'CNH',
    CNY: 'CNY',
    COP: 'COP',
    COU: 'COU',
    CRC: 'CRC',
    CUC: 'CUC',
    CUP: 'CUP',
    CVE: 'CVE',
    CZK: 'CZK',
    DJF: 'DJF',
    DKK: 'DKK',
    DOP: 'DOP',
    DZD: 'DZD',
    EEK: 'EEK',
    EGP: 'EGP',
    ERN: 'ERN',
    ETB: 'ETB',
    // ETH: 'ETH',
    EUR: 'EUR',
    FJD: 'FJD',
    FKP: 'FKP',
    GBP: 'GBP',
    GEL: 'GEL',
    GGP: 'GGP',
    GHC: 'GHC',
    GHS: 'GHS',
    GIP: 'GIP',
    GMD: 'GMD',
    GNF: 'GNF',
    GTQ: 'GTQ',
    GYD: 'GYD',
    HKD: 'HKD',
    HNL: 'HNL',
    HRK: 'HRK',
    HTG: 'HTG',
    HUF: 'HUF',
    IDR: 'IDR',
    ILS: 'ILS',
    IMP: 'IMP',
    INR: 'INR',
    IQD: 'IQD',
    IRR: 'IRR',
    ISK: 'ISK',
    JEP: 'JEP',
    JMD: 'JMD',
    JOD: 'JOD',
    JPY: 'JPY',
    KES: 'KES',
    KGS: 'KGS',
    KHR: 'KHR',
    KMF: 'KMF',
    KPW: 'KPW',
    KRW: 'KRW',
    KWD: 'KWD',
    KYD: 'KYD',
    KZT: 'KZT',
    LAK: 'LAK',
    LBP: 'LBP',
    LKR: 'LKR',
    LRD: 'LRD',
    LSL: 'LSL',
    LTC: 'LTC',
    LTL: 'LTL',
    LVL: 'LVL',
    LYD: 'LYD',
    MAD: 'MAD',
    MDL: 'MDL',
    MGA: 'MGA',
    MKD: 'MKD',
    MMK: 'MMK',
    MNT: 'MNT',
    MOP: 'MOP',
    MRO: 'MRO',
    MRU: 'MRU',
    MUR: 'MUR',
    MVR: 'MVR',
    MWK: 'MWK',
    MXN: 'MXN',
    MXV: 'MXV',
    MYR: 'MYR',
    MZN: 'MZN',
    NAD: 'NAD',
    NGN: 'NGN',
    NIO: 'NIO',
    NOK: 'NOK',
    NPR: 'NPR',
    NZD: 'NZD',
    OMR: 'OMR',
    PAB: 'PAB',
    PEN: 'PEN',
    PGK: 'PGK',
    PHP: 'PHP',
    PKR: 'PKR',
    PLN: 'PLN',
    PYG: 'PYG',
    QAR: 'QAR',
    RMB: 'RMB',
    RON: 'RON',
    RSD: 'RSD',
    RUB: 'RUB',
    RWF: 'RWF',
    SAR: 'SAR',
    SBD: 'SBD',
    SCR: 'SCR',
    SDG: 'SDG',
    SEK: 'SEK',
    SGD: 'SGD',
    SHP: 'SHP',
    SLL: 'SLL',
    SOS: 'SOS',
    SRD: 'SRD',
    SSP: 'SSP',
    STD: 'STD',
    STN: 'STN',
    SVC: 'SVC',
    SYP: 'SYP',
    SZL: 'SZL',
    THB: 'THB',
    TJS: 'TJS',
    TMT: 'TMT',
    TND: 'TND',
    TOP: 'TOP',
    TRL: 'TRL',
    TRY: 'TRY',
    TTD: 'TTD',
    TVD: 'TVD',
    TWD: 'TWD',
    TZS: 'TZS',
    UAH: 'UAH',
    UGX: 'UGX',
    USD: 'USD',
    UYI: 'UYI',
    UYU: 'UYU',
    UYW: 'UYW',
    UZS: 'UZS',
    VEF: 'VEF',
    VES: 'VES',
    VND: 'VND',
    VUV: 'VUV',
    WST: 'WST',
    XAF: 'XAF',
    // XBT: 'XBT',
    XCD: 'XCD',
    XOF: 'XOF',
    XPF: 'XPF',
    XSU: 'XSU',
    XUA: 'XUA',
    YER: 'YER',
    ZAR: 'ZAR',
    ZMW: 'ZMW',
    ZWD: 'ZWD',
    ZWL: 'ZWL',
} as const

type ObjectValues<T> = T[keyof T]

export type CurrencyCodesType = ObjectValues<typeof CurrencyCodesTypeMapped>

export type BrandCurrency = {
    code: CurrencyCodesType
    symbol: string
}

export type CurrencyPrices = {
    annualConverted: number,
    monthlyConverted: number,
}

/** Interface for Facebook Pixel conversion events */
export interface PixelConversionEvent {
    id?: string;
    name: string;
    count?: number;
    parent_name?: string | null;
    parent_id?: string | null;
}

/** Default conversion events available */
export const DefaultPixelConversionEventNames: Record<string, string> = {
    StreamingLinkClicked: 'StreamingLinkClicked',
    TourLinkClicked: 'TourLinkClicked',
    Subscribe: 'Subscribe',
    PreSaveSuccess: 'Pre-save Success'
}

export type LimitedOrganizationRoles = typeof OrganizationRole.ADMINISTRATOR | typeof OrganizationRole.MEMBER

export interface TeamMember {
    id: number
    name?: string
    lastName?: string
    email: string
    thumbnailUrl?: string
    role: OrganizationRole
    brands: CurrentBrand[]
    isCurrentUser?: boolean
}

export interface TeamPricesData {
    priceSeat: number
    priceSeatRemaining: number
    remainingDays: number
}

export interface OrganizationCampaign {
    status: CampaignStatus
    campaignName: string
    type: CampaignType
    spent: number
    CPC?: number
    CPV?: number
    playlistingCampaigns?: {
        pitchedPlaylists?: any
        followersAcrossPlaylists?: any
    }
    presaves?: {
        totalPresaves: number
        emailsCollected: number
    }
    budget: number
    endDate: string
    startDate: string
    conversionsEnabled: boolean
    brandId: number
    brandName: string
    brandImage: string
}

export interface PlatformsPreview {
    [key: string]: PlatformPreview
}

export interface PlatformPreview {
    name: string
    key: string
    goal: string
    selected: boolean
    subtitle: any
    uploadingAssets: boolean
}

export interface FacebookMeResponse {
    id: string;
    name: string;
    picture: { data: { url: string } };
    access_token: string;
    expires_at?: string | null;
    fbTokenValid: boolean;
}

type PlatformCaptions = {
    /** General caption used if platform-specific one isn't provided */
    all: string | null;
    spotify: string | null;
    apple_music: string | null;
    youtube: string | null;
    soundcloud: string | null;
    custom: string | null;
    deezer: string | null;
    tidal: string | null;
    amazon_store: string | null;
    amazon_music: string | null;
    instagram: string | null;
    tiktok: string | null;
};

interface MediaAsset {
    thumbnail: string;
    cloudinary_id: string;
    height: number;
    width: number;
    name: string;
    url: string;
    formatType: string;
    fb_id: string;
    type: 'video' | 'image';
}

export enum MediaProductType {
    REELS = "REELS",
    FEED = "FEED",
}

export enum MediaType {
    VIDEO = "VIDEO",
    CAROUSEL_ALBUM = "CAROUSEL_ALBUM",
    IMAGE = "IMAGE",
}

/** Interface for Instagram post media children */
interface IgMediaChild {
    id: string;
    ig_id: string;
    media_type: string;
    media_url: string;
    thumbnail_url?: string;
}

/** Interface for a search platform interest result */
export interface SearchPlatformInterestsResult {
    id: string,
    name: string,
    audience_size: number
    type: TargetedAudienceType
}

/** Interface for a targeting audience group with tags */
export interface TargetingAudienceGroup {
    /** Unique identifier for the audience group */
    id: string;
    /** Title/name of the audience group */
    title: string;
    /** Array of tags associated with this audience group */
    tags: Array<{
        /** Unique identifier for the tag */
        id: number | string;
        /** Display text for the tag */
        title: string;
        /** Audience size for the tag */
        audience_size?: number;
        /** Type of audience group */
        type: TargetedAudienceType;
    }>;
}

export interface InstagramPost {
    id: string;
    ig_id: string;
    fb_id?: string;
    permalink: string;
    timestamp: string;
    caption: string;
    explicit_content?: boolean;
    media_product_type: MediaProductType;
    media_type: MediaType
    like_count: number;
    comments_count: number;
    media_url?: string;
    thumbnail_url?: string;
    is_shared_to_feed?: boolean;
    children?: any;
    username?: string;
    selectable: boolean;
    platformType: AssetPlatformType.INSTAGRAM;
}

export type InstagramPostInput = {
    posts: InstagramPost[];
    setSelectedInstagramPost: any;
    selectedInstagramPost?: InstagramPost;
    showCaption: boolean;
    allowPostPreviews?: boolean;
}

/** Base interface for all campaign inputs with common properties */
export interface BaseCampaignInput {
    /** Current brand context */
    brand?: CurrentBrand;
    /** Campaign draft ID if editing existing draft */
    draft?: string | null;
    /** Campaign objective/goal */
    goal: string;
    /** Campaign budget amount in cents */
    budget: number;
    /** Campaign start date */
    startDate: string | Date;
    /** Campaign end date */
    endDate: string | Date;
    /** Campaign media assets */
    assets: MediaAsset[] | {
        all: MediaAsset[];
        spotify: MediaAsset[] | null;
        apple: MediaAsset[] | null;
        youtube: MediaAsset[] | null;
        custom: MediaAsset[];
    };
    /** Ad caption/copy text */
    caption: string | null;
    /** Campaign name */
    campaignName?: string;
    /** Call-to-action configuration */
    cta?: {
        value: string;
        [key: string]: any;
    };
    /** Facebook Ad Account details */
    fbAdAccount?: AdAccountPageType;
    /** Selected Facebook Page */
    fbPage?: FacebookPageType;
    /** Connected Instagram Page */
    igPage?: InstagramPageType;
    /** Optional Instagram media post */
    igMedia?: InstagramPost;
    /** Interest-based targeting audiences */
    interestTargetingAudiences: Array<{
        fbId: string;
        name: string;
    }>;
    /** Geographic targeting configuration */
    targetedCountries: {
        locations: Location[];
        id?: string;
        name: string;
        budget: number;
        description?: string;
    }[];
    /** Demographic targeting settings */
    targetingDemographics: DemographicsTargetingType;
    /** Custom audience targeting */
    customAudiences?: FBCustomAudience[];
    /** Additional page audiences for targeting */
    additionalPageAudiences?: FacebookPageResponse[];
    /** Organization data if applicable */
    primaryOrganization?: OrganizationData;
    /** Whether conversion tracking is enabled */
    conversionsEnabled?: boolean;
    /** Conversion event name if enabled */
    conversionsEventName?: string;
    /** Payment method for the campaign */
    payment_method?: string;
}

/** Facebook campaign specific properties */
export interface FacebookCampaignInput extends BaseCampaignInput {
    /** Type of campaign being created */
    campaign_type: string;

    /** Whether the campaign uses express setup flow */
    usesExpressSetup?: boolean;

    /** Unique identifier for the campaign */
    id?: number;
    /** Record/song information for music campaigns */
    record?: BrandContent;
    /** Platform-specific captions */
    captions?: PlatformCaptions;
    /** Campaign media assets by platform */
    assets: {
        all: MediaAsset[];
        spotify: MediaAsset[] | null;
        apple: MediaAsset[] | null;
        youtube: MediaAsset[] | null;
        custom: MediaAsset[];
    };
    /** Optional link for link click campaigns */
    link?: string | null;
    /** Additional links if needed */
    links?: any[];
    /** Facebook pixel configuration */
    fbPixel?: FBPixel;
    /** Whether visual content has been selected */
    addVisualSelected?: boolean;
    /** Facebook targeting suggestions */
    fbSuggestions?: FbSuggestion[];
    /** Campaign subtype */
    subtype?: string;
}

/** Link clicks campaign specific properties */
export interface LinkClicksCampaignInput extends FacebookCampaignInput {
    /** Target URL for the ad */
    link: string;
    /** Facebook pixel configuration */
    fbPixel: any; // TODO: Add proper pixel type
}

/** Instagram growth campaign specific properties */
export interface GrowInstagramCampaignInput extends FacebookCampaignInput {
    /** Campaign subtype configuration */
    selectedCampaignType: CampaignOption;
    /** Campaign type */
    campaign_type: string;
}

/** Parameters for duplicating a campaign */
export interface DuplicateCampaignParams {
    /** ID of the campaign to duplicate */
    campaignId: number;
    /** New project data for the duplicated campaign */
    projectData?: DuplicateSongAdsRecord | string | boolean;
}

/** Response from duplicate campaign API call */
export interface DuplicateCampaignResponse {
    /** Response data if successful */
    data: CreatedCampaign | null;
    /** Error object if request failed */
    error: unknown | null;
}

export interface BrandsToBeAddedToOrganization {
    brandId: number;
    currentTier: "pro" | "free";
    upgradeToPro: boolean;
}

export type PlaybookStep = {
    name: PlaybookStepName;
    title: string;
    isNoBackground?: boolean;
    middleContent: JSX.Element[];
    leftSideContent?: {
        title?: string;
        component: JSX.Element
    }
    rightSideContent?: {
        title?: string;
        component: JSX.Element
    };
}

export interface Playbook {
    title: string;
    description: string;
    steps: PlaybookStep[];
    adPlatform: AdPlatform;
}

export interface MappedColumns {
    Name?: string;
    LastName?: string;
    Email?: string;
    CellPhone?: string;
    Source?: string;
    City?: string;
    State?: string;
    Country?: string;
    [key: string]: string | undefined;
}

export interface FetchFanbaseParams {
    targeting?: boolean,
    city?: string,
    state?: string,
    country?: string,
    platform?: string,
    limit?: number,
    offset?: number,
    orderBy?: string,
    direction?: string,
    subscribed?: boolean,
}

export interface FansMetrics1 {
    fanbaseGrowth: string,
    foreverPresaveFans: number
    foreverPresaveFansGrowth: string,
    totalFanbase: number,
}

/**
 * Type definition for title alignment theme style options
 * @interface TitleAlignmentThemeStyleType
 * @property {string} title - Display title for the alignment option
 * @property {string} name - Identifier name for the alignment option
 * @property {number} order - Order in which the alignment option should appear
 */
export type TitleAlignmentThemeStyleType = {
    title: string;
    name: string;
    order: number;
};/**
 * Type representing the possible title alignment values
 * @type {string}
 */

export type TitleAlignment = "left" | "center" | "right";
/**
 * Interface defining the structure of a font theme style
 */
export interface FontThemeStyle {
    /** Display title of the font */
    title: string;
    /** Unique identifier for the font */
    name: string;
    /** Order in which the font appears in the selector */
    order: number;
    /** Whether this font is only available to Pro users */
    isPaidOnly: boolean;
}
/**
 * Type defining the available font theme style keys
 */

export type FontThemeStyleType = keyof typeof fontThemeStyles;

export enum WEBSITE_TABS {
    SETUP = 'setup',
    STYLE = 'style',
    CONNECT = 'connect',
    ANALYTICS = 'analytics'
}

export type StreamingService = 'spotify' | 'apple_music' | 'amazon_music' | 'youtube' | 'deezer' | 'tidal' | 'soundcloud' | 'audiomack' | 'yandex' | 'tiktok';
export type SocialMediaType = (typeof SocialMedia)[keyof typeof SocialMedia];
export enum WEBSITE_TABS_VIEW_ONLY {
    ANALYTICS = 'analytics'
}
export interface bgValueType {
    color: string;
    artwork: number;
}
export interface ScrollPosition {
    customize: number;
    preview: number;
}

export interface DefaultStyles {
    removeSymphonyBrand: boolean;
    enableSocialIcons: boolean;
    socialIcons: WebsiteLink[];
    subtitle: string;
    titleText: string;
    mainColor: string;
    secondaryColor: string;
    bgValue: bgValueType;
    bgType: string;
    styleType: string;
    youtubeEmbed: {
        id: string;
        url: string;
    };
}

export interface WebsiteLinks {
    [key: string]: WebsiteLink;
}

export interface StyleType {
    title: string;
    name: string;
    order: number;
}


export interface AlbumTrack {
    name: string;
    explicit: boolean;
    record_url: string;
    spotify_id: string;
    preview_url?: string;
}

export interface WebsiteLink {
    enabled: boolean;
    rounded: boolean;
    backgroundColor: string;
    iconColor?: string;
    height: string;
    width: string;
    url: string;
    key: string;
    title: string;
    type?: string;
    placeholder: string;
}
export type WebsiteDataType = {
    contentSlug: string;
    since: string;
    until: string;
    topLevelMetrics: TopLevelMetrics;
    referrers: Referrer[];
    overallActivity: OverallActivity[];
    geographicBreakdown: GeographicBreakdown;
    contentBreakdown: any[]; // Specify further if there's a known structure
    socialMediaBreakdown: any[]; // Specify further if there's a known structure

    // for music
    streamingServicesBreakdown?: any[];
    // for tour
    tourLinkClicksBreakdown?: TourLinkClicksBreakdown[];
    presaleActionBreakdowns?: TourLinkClicksBreakdown[];

    // for presave - used to show daily stats on the presave dashboard
    dailyStats?: {
        // The date of the daily stat entry
        date: string;
        // Number of new pre-saves for this date
        newPresaves: number;
        // Number of new forever saves for this date
        newForeverSaves: number;
        // Cumulative total of forever saves up to this date
        totalForeverSaves: number;
        // Cumulative total of pre-saves up to this date
        totalPresaves: number;
        // Number of page visits for this date
        pageVisits: number;
    }[];
};
export enum WEBSITE_CREATION_TYPE {
    AUTOMATIC = 'automatic',
    MANUAL = 'manual',
    PRESAVE = 'presave',
    CAMPAIGN = 'marketing'
}

export const RECORD_STREAMS_TYPES = {
    PROJECT: "project",
    PLAYLIST: "playlist",
    TRACK: "track",
} as const;

export type RecordStreamsType = (typeof RECORD_STREAMS_TYPES)[keyof typeof RECORD_STREAMS_TYPES];


export type TourLinkClicksBreakdown = {
    count: number;
    city: string;
    date: string;
    venue: string;
};
// used to show overall numbers on the website dashboard

export type TopLevelMetrics = {
    pageVisits: number;
    ctr: number;
    // for music
    streamingLinkClicks?: number;
    // for tour
    tourLinkClicks?: number;
    // for data collector
    signups?: number;
    // for presave
    newPresaves?: number;
    newForeverSaves?: number;
    totalPresaves?: number;
    totalForeverSaves?: number;
};

export type Referrer = {
    referrer: string | null;
    count: number;
};

export type OverallActivity = {
    streamingLinkClicks?: number;
    tourLinkClicks?: number;
    signups?: number;
    pageViews: number;
    date: string;
};

export type GeographicBreakdown = {
    cities?: LocationCount[];
    countries?: LocationCount[];
};

export type LocationCount = {
    country: string;
    city?: string;
    count: number;
    uniqueId?: string;
};
export const websiteLinkInitObj: WebsiteLink = {
    enabled: true,
    rounded: true,
    backgroundColor: "",
    height: "25",
    width: "25",
    url: "",
    key: "",
    title: "",
    placeholder: "",
};

export const StreamingLinkTypeOptions: WebsiteLink[] = [
    {
        ...websiteLinkInitObj,
        ...{ key: 'spotify', title: 'Spotify' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'apple_music', title: 'Apple Music' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'amazon_music', title: 'Amazon Music' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'youtube', title: 'YouTube Music' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'deezer', title: 'Deezer' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'tidal', title: 'Tidal' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'soundcloud', title: 'Soundcloud' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'audiomack', title: 'Audiomack' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'boomplay', title: 'Boomplay' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'anghami', title: 'Anghami' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'bandcamp', title: 'Bandcamp' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'beatport', title: 'Beatport' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'google_play', title: 'Google Play' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'pandora', title: 'Pandora' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'tiktok', title: 'TikTok' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'soundxyz', title: 'Sound.xyz' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'shazam', title: 'Shazam' },
    },
    {
        ...websiteLinkInitObj,
        ...{ key: 'twitter', title: 'Twitter' },
    },
];
export const SocialMedia = {
    FACEBOOK: 'facebook',
    INSTAGRAM: 'instagram',
    TIKTOK: 'tiktok',
    TWITTER: 'twitter',
    YOUTUBE: 'youtube',
} as const;

/** Spotify album search result */
export interface SpotifyAlbum {
    /** Album name */
    name: string;
    /** Comma-separated list of artist names */
    artists: string;
    /** Primary artist name */
    primary_artist: string;
    /** Album release date */
    release_date: string;
    /** URL to album thumbnail image */
    thumbnail_url: string;
    /** Number of tracks in the album */
    track_count: number;
    /** Spotify URL to the album */
    spotify_url: string;
    explicit?: boolean;
    spotify_id: string;
}

/** Spotify track search result */
export interface SpotifyTrack {
    /** Track name */
    name: string;
    /** Comma-separated list of artist names */
    artists: string;
    /** Primary artist name */
    primary_artist: string;
    /** Name of the album this track belongs to */
    album_name: string;
    /** Track release date */
    release_date: string;
    /** URL to track thumbnail image */
    thumbnail_url: string;
    /** Spotify URL to the track */
    spotify_url: string;
    explicit?: boolean;
    spotify_id: string;
}

/** Spotify playlist search result */
export interface SpotifyPlaylist {
    /** Number of tracks in the playlist */
    track_count: number;
    /** Playlist type identifier */
    type: 'playlist';
    /** Spotify ID in a different format */
    spotify_id: string;
    /** URL to playlist thumbnail image */
    thumbnail_url: string;
    /** Playlist name */
    name: string;
    /** Spotify URI for the playlist */
    spotify_uri: string;
    /** URL to the playlist */
    record_url: string;
    /** Primary artist associated with the playlist */
    primary_artist: string;
    /** Playlist owner name */
    owner: string;
    /** Whether the playlist contains explicit content */
    explicit: boolean;
    /** Array of track objects (optional) */
    tracks: null | any[];
    /** External links associated with the playlist */
    external_links: Array<{
        name: string;
        url: string;
    }>;
    /** URL to the playlist owner's profile */
    owner_url: string;
    /** Spotify ID of the playlist owner */
    owner_id: string;
    /** Playlist description */
    description: string;
    /** Spotify URL to the playlist */
    spotify_url: string;
}

/** Complete Spotify search response */
export interface SpotifyRecordSearchResponse {
    /** Array of album results */
    albums: SpotifyAlbum[];
    /** Array of track results */
    tracks: SpotifyTrack[];
    /** Array of playlist results */
    playlists: SpotifyPlaylist[];
}

export interface WebsiteAnalyticsData {
    topLevelStats: any;
    geographicBreakdown: any;
    contentAnalytics: any;
}

export interface CampaignOption {
    id: string;
    label: string;
    icon: string;
    description: string;
    recommendation: string;
    conversionGoal: string;
    tips: string;
    whenToUse?: string[];
    setupNeeded?: string[];
    conversionType: string;
    averageConversionCost: number;
    available?: boolean;
    availabilityNote?: string;
}


export type PreConnectionConfigs = {
    status: boolean
    custom_audience_accepted: {
        status: boolean
        action: null | string
        name: string
    }
    value_based_custom_audience_tos: {
        status: boolean
        action: null | string
        name: string
    }
    user_tos_accepted: {
        status: boolean
        action: null | string
        name: string
    }
    account_status: {
        status: number
        action: null | string
        name: string
    }
}
/** Unified record type for all Spotify content */
export interface UnifiedSpotifyRecord {
    name: string;
    thumbnailUrl: string;
    primaryArtist: string;
    type: RecordSelectorType;
    spotifyUrl?: string;
    explicit?: boolean;
    trackCount?: number;
    releaseDate?: string;
    parentName?: string;
    spotifyId: string;
    slug?: string;
}
